import { Component, OnDestroy, OnInit, inject } from '@angular/core';

import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';

import { translate } from '@jsverse/transloco';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';

import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { HasTitle } from 'app/has-title';
import { DatasourceManagerStore } from 'app/modules/dashboard/stores/datasource-manager.store';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { FullStoryService, PageNames } from 'app/modules/full-story/services/full-story.service';
import { UploadCreateDialogResult } from 'app/modules/upload/components/upload-create/upload-create.component';
import { AnalyticsService, TimerFn } from 'app/providers/analytics.service';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { DashboardService } from 'app/providers/bridge-api/dashboard.service';
import {
  RealtimeConnection,
  RealtimeMessage,
  RealtimeService,
} from 'app/providers/realtime.service';
import { AnalysisGroupService } from 'app/providers/storage-api/analysis-group-service';
import { AnalysisService } from 'app/providers/storage-api/analysis.service';
import { Collection, CollectionService } from 'app/providers/storage-api/collection.service';
import { FieldService } from 'app/providers/storage-api/field.service';
import { PredictiveService } from 'app/providers/storage-api/predictive.service';
import { Project, ProjectService, User } from 'app/providers/storage-api/project.service';
import { UploadService } from 'app/providers/storage-api/upload.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import { IconService } from 'app/services/icon.service';
import { LoggerService } from 'app/services/logger.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { RtrEventHandlerService } from 'app/services/rtr-event-handler.service';
import { SystemService } from 'app/services/system.service';
import { Utils } from 'app/utils/utils';

import { GroupService } from '../../../../providers/semantria-api/group.service';

import { ContentChannelService } from '../../../../providers/storage-api/content-channel.service';
import { ProjectListHandlerService } from '../project-list/project-list-handler.service';
import { ProjectDetailService } from '../../services/project-detail.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-project-view',
  templateUrl: './project-view.component.html',
  styleUrls: ['./project-view.component.scss'],
  standalone: false,
  providers: [DatasourceManagerStore],
})
export class ProjectViewComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  private projectId: string;
  private groupId: string;

  public project: Project;
  private readonly datasourceManagerStore = inject(DatasourceManagerStore);
  public isLoading: boolean;
  public languageList: string[] = [];

  public owner: User;
  public openDrawer: boolean;

  private session: Session;
  private skipLoad = false;
  private debounceProjectUpdate$ = new Subject();
  isExecutive: boolean;
  realtimeHandlers: RealtimeConnection[] = [];
  reportTiming: TimerFn;
  projectRefreshSub$: Subscription;
  collectionUploadSub$: Subscription;

  projectNames: string[] = [];
  projectIconSource: string;
  projectNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };
  projectDescriptionStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };

  isUploadCreateDialogOpen = false;
  private projectListHandlerService = inject(ProjectListHandlerService);

  private currentTabLabel: string | undefined;
  private readonly tabTitleKeys: Readonly<Record<string, string>> = {
    all: 'project.project-details-table.all',
    dashboard: 'project.project-details-table.dashboards',
    collection: 'project.project-details-table.collections',
    group: 'project.project-details-table.analysis-groups',
  };

  constructor(
    protected titleService: Title,
    private route: ActivatedRoute,
    private router: Router,
    private dialogService: DialogService,
    private projectService: ProjectService,
    private alertService: AlertService,
    private authService: AuthService,
    private dashboardService: DashboardService,
    private collectionService: CollectionService,
    private analysisService: AnalysisService,
    private groupService: GroupService,
    private analysisGroupService: AnalysisGroupService,
    private uploadService: UploadService,
    private fullStoryService: FullStoryService,
    private iconService: IconService,
    private systemService: SystemService,
    private googleAnalyticsService: AnalyticsService,
    private logger: LoggerService,
    private projectDetailService: ProjectDetailService,
    private predictiveService: PredictiveService,
    private fieldService: FieldService,
    private customFieldService: CustomFieldsService,
    private contentChannelService: ContentChannelService,
    private rtrHandlerService: RtrEventHandlerService
  ) {
    super(titleService);

    this.reportTiming = this.googleAnalyticsService.createTimer('Projects');
  }

  async ngOnInit() {
    this.projectRefreshSub$ = this.projectDetailService.projectRefresh.subscribe(() => {
      this.loadProject();
    });
    this.collectionUploadSub$ = this.projectDetailService.collectionUpload.subscribe((coll) => {
      this.goToCollectionUpload(coll);
    });
    this.debounceProjectUpdate$.pipe(debounceTime(1000)).subscribe(() => {
      this.project = Object.assign({}, this.project);
    });

    this.route.params
      .pipe(distinctUntilChanged((a, b) => a['projectId'] === b['projectId']))
      .subscribe((params) => {
        this.projectId = params.projectId;
        this.init();
        this.setProjectNames();
      });

    this.route.params
      .pipe(distinctUntilChanged((a, b) => a['tab'] === b['tab']))
      .subscribe((params) => {
        const tabKey = params['tab'] as string | undefined;
        this.currentTabLabel = tabKey ? translate(this.tabTitleKeys[tabKey]) : undefined;
        this.updateTitle();
      });
    this.openDrawer = false;

    this.reportTiming('project_collections_loaded');

    if (!this.groupService.getSelectedGroup()) {
      await this.groupService.refreshAndSelectActiveGroup();
    }
  }

  ngOnDestroy() {
    this.unsubscribeFromRealtimeHandlers();
    this.projectRefreshSub$?.unsubscribe();
    this.collectionUploadSub$?.unsubscribe();
    this.debounceProjectUpdate$?.unsubscribe();
  }

  public async setProjectNames(): Promise<void> {
    const projectList = await this.projectService.getProjects();

    if (this.project) {
      this.projectNames = projectList.items
        .map((p) => p.name)
        .filter((name) => name !== this.project.name);
    }
  }

  async init() {
    const url = this.route.snapshot;
    this.groupId = url.parent.paramMap.get('groupId');

    this.isLoading = true;
    try {
      const requests = [this.loadSession()];
      if (!this.skipLoad) {
        requests.push(this.loadProject());
      }
      await Promise.all(requests);
      if (!this.skipLoad) {
        this.unsubscribeFromRealtimeHandlers();
        this.subscribeToRealtimeHandlers();
        const loadTime = this.reportTiming('project_loaded');
        this.fullStoryService.setPageVars(PageNames.project, {
          projectId: this.project.id,
          loadTime,
        });
        this.isLoading = false;
      }
    } catch (error) {
      if (error.status === 404) {
        this.logger.error('Failed to get project', { error });
        const defaultURL = `/groups/${this.groupService.getSelectedGroupId(true)}/projects/${
          this.route.paramMap['destination'].value['projectId']
        }`;
        if (this.router.url === defaultURL) {
          //if the current url is the default URL then it's a problem // doesn't set the homepage to anything, user has to do that after being redirected.
          await this.groupService.refreshAndSelectActiveGroup();
          const groupId = this.groupService.getSelectedGroupId(true);
          const newURL = `/groups/${groupId}/projects`;
          await this.router.navigateByUrl(newURL, { skipLocationChange: true });
        }
        await this.router.navigateByUrl('not-found', { skipLocationChange: true });
      } else {
        this.logger.error('project-view ngOnInit error', { error });
      }
    }
  }

  subscribeToRealtimeHandlers() {
    this.realtimeHandlers = [
      this.projectService.on(this.projectId, this.handleProjectEvent.bind(this)),
      this.collectionService.on(this.projectId, this.handleCollectionsEvent.bind(this)),
      this.uploadService.on(this.projectId, this.handleUploadsEvent.bind(this)),
      this.analysisService.on(this.projectId, this.handleAnalysesEvent.bind(this)),
      this.analysisGroupService.on(this.projectId, this.handleAnalysisGroupsEvent.bind(this)),
      this.dashboardService.on(this.projectId, this.handleDashboardEvent.bind(this)),
    ];
  }

  unsubscribeFromRealtimeHandlers() {
    this.realtimeHandlers.forEach((handler) => {
      if (handler) {
        handler.disconnect();
      }
    });
  }

  async loadProject() {
    this.isLoading = true;
    const project = await this.projectService.getProjectById(this.projectId);
    this.datasourceManagerStore.initialize({ analyses: project.analyses, project });
    await this.datasourceManagerStore.loadDataSourcesFromProject(project);

    this.project = project;

    this.projectIconSource = this.iconService.getProjectIcon(this.project.iconUrl);
    this.updateTitle();
    this.isLoading = false;
    this.projectListHandlerService.shouldListBeDisabled.set(false);

    if (!this.project.collections?.length) {
      if (!this.isReadOnly) {
        this.goToCollectionUpload(undefined);
      }
    }
  }

  private handleCollectionsEvent(data: RealtimeMessage<{ id: string; projectId: string }>) {
    this.logger.debug('handleCollectionsEvent', { data });
    const idx = this.project.collections.findIndex((c) => c.collectionName === data.object.id);
    if (data.verb === 'DELETE') {
      if (idx !== -1) {
        const deletedCollection = this.project.collections.splice(idx, 1);
        this.project.collectionCount = this.project.collections.length;
        this.projectDetailService.onCollectionEvent(data.verb, deletedCollection[0]);
      }
      this.projectDetailService.onAnalysisGroupCollectionEvent(data.verb, data.object.id);
      return;
    }
    if (RealtimeService.isLogicallyDeleted(data)) {
      if (idx !== -1) {
        this.project.collections.splice(idx, 1);
        this.project.collectionCount = this.project.collections.length;
      }
    } else {
      this.rtrHandlerService.handleCollectionRtrEvent(data).then((collection) => {
        const updatedIdx = this.project.collections.findIndex(
          (c) => c.collectionName === data.object.id
        );
        if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
          if (updatedIdx === -1) {
            this.project.collections.push(collection);
          } else {
            this.project.collections[updatedIdx] = collection;
          }
        }

        this.project.collectionCount = this.project.collections.length;

        if (data.verb === 'UPDATE') {
          this.projectDetailService.onCollectionEvent(data.verb, collection);
        } else {
          this.project = Object.assign({}, this.project);
        }
      });
    }
  }

  private handleAnalysisGroupsEvent(data: RealtimeMessage<{ id: string; projectId: string }>) {
    this.logger.debug('handleAnalysisGroupsEvent', { data });
    const idx = this.project.groups.findIndex((g) => g.id === data.object.id);
    if (data.verb === 'DELETE') {
      const deletedItem = this.project.groups.splice(idx, 1);
      this.projectDetailService.onAnalysisGroupEvent(data.verb, deletedItem[0]);
      return;
    }
    this.rtrHandlerService.handleAnalysisGroupRtrEvent(data).then((group) => {
      if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
        // We need to recheck the idx of the elements b/c async stuff for details see: DASH-9130
        const idx_recheck = this.project.groups.findIndex((g) => g.id === data.object.id);
        if (idx_recheck === -1) {
          this.project.groups.push(group);
        } else {
          this.project.groups[idx_recheck] = group;
        }
      }
      if (data.verb !== 'UPDATE') {
        this.project = Object.assign({}, this.project);
      }
      this.projectDetailService.onAnalysisGroupEvent(data.verb, group);
    });
  }

  private handleDashboardEvent(data: RealtimeMessage<{ id: string; projectId: string }>) {
    this.logger.debug('handleDashboardEvent', { data });
    const idx = this.project.dashboards.findIndex((d) => d.id === data.object.id);

    if (data.verb === 'DELETE') {
      this.project.dashboards.splice(idx, 1);
      return;
    }
    this.rtrHandlerService.handleDashboardRtrEvent(data).then((dashboard) => {
      // We need to recheck the idx of the elements b/c async stuff for details see: DASH-9130
      const idx_recheck = this.project.dashboards.findIndex((d) => d.id === data.object.id);
      if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
        if (idx_recheck === -1) {
          this.project.dashboards.push(dashboard);
        } else {
          this.project.dashboards[idx_recheck] = dashboard;
        }
      }
      //Remove hidden dashboards from project display for non-owners after change (without needing page refresh)
      if (!this.isAdmin) {
        this.project.dashboards = this.project.dashboards.filter(
          (dash) => dash.visibility !== 'UNLISTED' || dash.creator === this.session.user.user_name
        );
      }
      this.project = Object.assign({}, this.project);
    });
  }

  private handleAnalysesEvent(
    data: RealtimeMessage<{ id: string; collectionId: string; projectId: string }>
  ) {
    this.logger.debug('handleAnalsysesEvent', { data });

    const idx = this.project.analyses.findIndex(
      (a) => a.name === data.object.id && a.collectionName === data.object.collectionId
    );
    const cidx = this.project.collections.findIndex(
      (c) => c.collectionName === data.object.collectionId
    );
    const groups = this.project.groups
      .map((group) => {
        const gaidx = group.analyses.findIndex(
          (ga) =>
            ga.analysisName === data.object.id && ga.collectionName === data.object.collectionId
        );
        if (gaidx !== -1) {
          return {
            group,
            idx: gaidx,
          };
        }
      })
      .filter((o) => o);
    if (data.verb === 'DELETE') {
      this.project.analyses.splice(idx, 1);
      if (cidx !== -1) {
        this.project.collections[cidx].analysisCount--;
      }
      this.project = Object.assign({}, this.project);
      return;
    }
    this.rtrHandlerService.handleAnalysisRtrEvent(data).then((analysis) => {
      if (
        data.verb === 'UPDATE' &&
        idx !== -1 &&
        analysis?.status === 'IDLE' &&
        this.project.analyses[idx].status === 'OPEN'
      ) {
        this.project = Object.assign({}, this.project);
      }
      if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
        if (idx === -1) {
          this.project.analyses.push(analysis);
          if (cidx !== -1) {
            this.project.collections[cidx].analysisCount++;
          }
        } else {
          this.project.analyses[idx] = analysis;

          for (const group of groups) {
            group.group.analyses[group.idx] = analysis;
          }
        }
      }

      //Only refresh table if analysis not actively updating
      if (
        analysis?.status !== 'OPEN' &&
        analysis?.status !== 'QUEUED' &&
        analysis?.status !== 'IDLE' &&
        analysis?.status !== 'DELETED' &&
        analysis?.status !== 'DELETING'
      ) {
        this.project = Object.assign({}, this.project);
      }
      this.projectDetailService.onAnalysisEvent(data.verb, analysis);
    });
    this.project.analysesCount = this.project.analyses.length;
  }

  private handleUploadsEvent(
    data: RealtimeMessage<{ id: string; projectId: string; collectionId: string }>
  ) {
    this.logger.debug('handleUploadsEvent', { data });
    this.rtrHandlerService.handleUploadRtrEvent(data).then((upload) => {
      const idx = this.project.uploads.findIndex((u) => u.id === upload.id);
      if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
        if (idx === -1) {
          this.project.uploads.push(upload);
        } else {
          this.project.uploads[idx] = upload;
        }

        if (
          upload.state === 'COMPLETE' &&
          upload.metadata.columns.some((c) => c.field_name === 'sections')
        ) {
          this.fieldService.getFields(true);
        }
      } else if (data.verb === 'DELETE') {
        this.project.uploads.splice(idx, 1);
      }

      if (data.verb !== 'UPDATE') {
        this.project = Object.assign({}, this.project);
      }
    });
  }

  private handleProjectEvent(data: RealtimeMessage<Project>) {
    this.logger.debug('handleProjectEvent', { data });

    if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
      const dashboards = this.project.dashboards;
      const analyses = this.project.analyses;
      const uploads = this.project.uploads;
      const groups = this.project.groups;
      const collections = this.project.collections;
      this.project = data.object;
      this.project.dashboards = dashboards;
      this.project.analyses = analyses;
      this.project.uploads = uploads;
      this.project.groups = groups;
      this.project.collections = collections;
      this.setTitle(translate('project-view.projects', { name: this.project.name }));
    } else if (data.verb === 'DELETE') {
      this.dialogService
        .showSimpleDialog({
          title: translate('project-view.project-deleted'),
          content: translate('project-view.project-has-been-deleted'),
        })
        .afterClosed()
        .subscribe(async () => {
          this.router.navigate([`groups/${this.groupId}/projects`]);
        });
    }

    this.project = Object.assign({}, this.project);
  }

  openOptionsDrawer() {
    this.openDrawer = true;
  }

  onOptionsDrawerClosed() {
    this.projectIconSource = this.iconService.getProjectIcon(this.project.iconUrl);
    this.openDrawer = false;
  }

  async loadSession() {
    this.session = await this.authService.getSession();
    this.isExecutive = this.authService.isExecutive;
    this.checkUserAccess();
  }

  checkUserAccess() {
    if (!this.isAdmin && !this.authService.hasProjectAccess(this.session, this.projectId)) {
      this.skipLoad = true;
      this.router.navigateByUrl('not-found', { skipLocationChange: true });
    }
  }

  public get isAdmin() {
    return this.authService.isAccountAdmin;
  }

  public get isReadOnly() {
    return this.authService.isReadOnly;
  }

  public get isDebug() {
    return this.authService.isDebugUser;
  }

  public canEditProjectInfo(): boolean {
    return !this.isReadOnly && this.isAdmin;
  }

  public backToProjects(): void {
    this.router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() => this.router.navigate([`groups/${this.groupId}/projects`]));
  }

  downloadProjectData() {
    const projectStub = { ...this.project };
    delete projectStub.analyses;
    delete projectStub.uploads;
    delete projectStub.dashboards;

    this.downloadData(projectStub, 'data');
  }

  downloadAnalysisData() {
    this.downloadData(this.project.analyses, 'analyses');
  }

  private downloadData(data, fileSuffix) {
    const filename =
      this.project.name.replace(new RegExp('\\s', 'g'), '-') + '-' + fileSuffix + '.json';

    const file = new File([JSON.stringify(data, null, '  ')], filename, {
      type: 'text/plain; charset=utf-8',
    });

    this.systemService.saveFile(file);
  }

  public goToCollectionUpload(collection: Collection) {
    if (this.authService.isDemoMode) {
      this.dialogService.showDemoModeDialog();
      return;
    }

    if (collection) {
      this.router.navigate(
        [
          `groups/${this.groupId}/projects/${this.projectId}/collections/${collection.collectionName}/upload`,
        ],
        { queryParams: { displayName: collection.displayName } }
      );
      return;
    }

    this.isUploadCreateDialogOpen = true;
  }

  onCloseUploadCreateDialog(result: UploadCreateDialogResult | null): void {
    this.isUploadCreateDialogOpen = false;

    if (!result) {
      return;
    }

    if (result.showUpload) {
      this.router.navigate(
        [
          `groups/${this.groupId}/projects/${this.projectId}/collections/${result.collectionName}/upload`,
        ],
        { queryParams: { displayName: result.displayName } }
      );
    } else {
      this.loadProject();
    }
  }

  public async deleteProjectDialog(project: Project): Promise<void> {
    this.dialogService
      .showWarningPopup({
        title: translate('project-view.delete-project-confirm', {
          name: Utils.truncate(this.project.name, 30),
        }),
        content: translate('project-view.deleting-a-project'),
        confirmTitle: translate('project-view.delete-project'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.alertService.showToast(
            translate('project-view.deleting-project', { name: project.name })
          );
          try {
            await this.projectService.deleteProject(project.id);
          } catch (error) {
            this.alertService.showErrorToast(
              translate('project-view.failed-to-delete', { name: project.name }),
              error
            );
            return;
          }
          this.googleAnalyticsService.sendEvent('delete_project', 'Projects');
          this.backToProjects();
        }
      });
  }

  private updateTitle(): void {
    if (!this.project) {
      return;
    }
    const suffix = this.currentTabLabel ? `/${this.currentTabLabel}` : '';
    this.setTitle(`Projects: ${this.project.name}${suffix}`);
  }
}
